import React, { useMemo } from "react";
import { BaseModal, IModalInstance, modalService, ModalHeader } from "@whatslly/ui-components/modals";
import Toast from "@whatslly/ui-components/v2/Toaster/Toast";
import ms from "ms";
import { ModalButtons } from "@whatslly/ui-components/ModalButtons";
import styles from "./ContactOptInModal.module.scss";
import { useTranslation } from "react-i18next";
import { Select } from "@whatslly/ui-components/v2/BaseElements/Select";
import type { TemplateLanguages, TemplateHeaderMediaType } from "@whatslly/types/waba/WhatsappBusinessApiTemplate";
import { componentsToEditableComponents, editableComponentToParameter, getRequiredComponents, getHeaderMediaType, injectMediaHeaderComponent, WhatsappBusinessApiHeaderType } from "@whatslly/types/waba/WhatsappBusinessApiTemplate";
import { OptInResult, OptInTemplate } from "@whatslly/types/waba/WhatsappBusinessApiSelectChat";
import { TFunction } from "i18next";
import { observer, useLocalStore } from "mobx-react-lite";
import Mustache from "mustache";
import { TextInput } from "@whatslly/ui-components/v2/BaseElements/TextInput";
import { runInAction } from "mobx";
import { ConversationAssignmentCheckboxes } from "./components/ConversationAssignmentCheckboxes";
import { FileDropzone } from "@whatslly/ui-components/v2/FileDropzone/FileDropzone";
import { calFileSizeV2, formatBytes } from "@whatslly/commons/utils";
import { ALL_ACCEPT_FILE_TYPES_SIZE_MAP, getMediaAccept, MEDIA_HEADER_ICONS } from "./agentUtils";
import i18n from "./i18n/config";
import { LuX } from "react-icons/lu";

const WHATSAPP_PARAM_MAX_LENGTH = 1024;

interface IContactOptInModalProps extends IModalInstance<OptInResult> {
  templates: TemplateLanguages[];
  disableOptIn?: boolean;
  numberOfSelectedChats?: number;
}

const templateToOption = (template: TemplateLanguages) => ({ value: template, label: template.name });
const langToOption = (value: TemplateLanguages["languages"][number], t: TFunction) => ({
  value,
  label: t(`whatsapp_business_api_agent_page.template_langs.${value.language}`),
});

export const ContactOptInModal: React.FC<IContactOptInModalProps> = observer(({ closeModal, disableOptIn, templates, numberOfSelectedChats }) => {
  const { t } = useTranslation();
  const store = useLocalStore(() => ({
    assignConversationToMe: true,
    setConversationToOpen: true,
    headerMedia: null as { file: File; dataUrl: string } | null,

    get templates() {
      return templates.map(templateToOption);
    },
    get langs() {
      return store.selectedTemplate?.value.languages.map((lang) => langToOption(lang, t)) ?? [];
    },
    get editableComponents() {
      return store.components.filter((c) => c.example.length);
    },
    get headerMediaType(): WhatsappBusinessApiHeaderType | undefined {
      const components = store.selectedLang?.value.components;
      return getHeaderMediaType(components);
    },
    get result(): OptInResult | undefined {
      if (!store.selectedTemplate || !store.selectedLang) return undefined;

      const editableParams = store.editableComponents.map(editableComponentToParameter);
      const requiredButtons = getRequiredComponents(store.selectedLang.value.components);
      const baseComponents = [...editableParams, ...requiredButtons];
      const needsMediaHeader = store.headerMediaType && store.headerMedia;
      const components: OptInTemplate["components"] = needsMediaHeader
        ? injectMediaHeaderComponent(baseComponents, store.headerMediaType!.toLowerCase() as TemplateHeaderMediaType, "")
        : baseComponents;

      return {
        template: {
          name: store.selectedTemplate.value.name,
          language: store.selectedLang.value.language,
          components,
        },
        assignConversationToMe: store.assignConversationToMe,
        setConversationToOpen: store.setConversationToOpen,
        ...(needsMediaHeader && store.headerMedia
          ? {
              headerMedia: {
                binary: store.headerMedia.dataUrl.replace(/^data:[^;]+;base64,/, ""),
                mimeType: store.headerMedia.file.type,
                fileName: store.headerMedia.file.name,
              },
            }
          : {}),
      };
    },
    get allParams() {
      let globalIndex = 1;
      return store.components.flatMap((component) =>
        component.example.map((example, paramIndex) => ({
          component,
          paramIndex,
          globalIndex: globalIndex++,
          value: component.values[paramIndex + 1] ?? "",
          example,
        }))
      );
    },
    get previewButtons(): { text: string; type: string }[] {
      const components = store.selectedLang?.value.components;
      if (!components) return [];
      const buttonsComp = components.find((c) => c.type === "BUTTONS");
      if (!buttonsComp || buttonsComp.type !== "BUTTONS") return [];
      return buttonsComp.buttons.map((b) => ({ text: b.text, type: b.type }));
    },
    get previewTexts() {
      return store.components.map((component) => {
        const previewValues: { [key: number]: string } = {};
        store.allParams
          .filter((p) => p.component === component)
          .forEach((p) => {
            previewValues[p.paramIndex + 1] = p.value?.trim() ? p.value : `{{${p.globalIndex}}}`;
          });
        return Mustache.render(component.text, previewValues);
      });
    },
    get isValid(): boolean {
      const paramsValid = store.allParams.every((param) => param.value.trim().length > 0 && param.value.length <= WHATSAPP_PARAM_MAX_LENGTH);
      if (!paramsValid) return false;
      if (store.headerMediaType && !store.headerMedia) return false;
      return true;
    },
    getParamError(value: string): string | undefined {
      if (!value.trim()) return t("whatsapp_business_api_agent_page.opt_in.modal.param_required");
      if (value.length > WHATSAPP_PARAM_MAX_LENGTH)
        return t("whatsapp_business_api_agent_page.opt_in.modal.param_too_long", { max: WHATSAPP_PARAM_MAX_LENGTH });
      return undefined;
    },
    touched: new Set<number>(),
    setTouched(index: number) {
      store.touched.add(index);
    },
    selectedTemplate: templates[0] ? templateToOption(templates[0]) : undefined,
    selectedLang: templates[0]?.languages[0] ? langToOption(templates[0]?.languages[0], t) : undefined,
    components: componentsToEditableComponents(templates[0]?.languages[0]?.components),
    selectTemplate(selected: { label: string; value: TemplateLanguages }) {
      store.selectedTemplate = selected;
      store.selectedLang = selected.value.languages[0] ? langToOption(selected.value.languages[0], t) : undefined;
      store.components = componentsToEditableComponents(selected.value.languages[0]?.components);
      store.touched.clear();
      store.headerMedia = null;
    },
    selectLang(selected: { label: string; value: TemplateLanguages["languages"][number] }) {
      store.selectedLang = selected;
      store.components = componentsToEditableComponents(selected.value.components);
      store.touched.clear();
      store.headerMedia = null;
    },
    onMediaFileSelect(dataUrl: string, file: File) {
      const sizeLimit = ALL_ACCEPT_FILE_TYPES_SIZE_MAP[file.type];
      if (sizeLimit && file.size > sizeLimit) {
        modalService.openModal(Toast, {
          text: i18n.t("whatsapp_business_api_agent_page.opt_in.modal.file_too_large", { maxSize: formatBytes(sizeLimit) }),
          type: "error",
          timeout: ms("10s"),
        });
        return;
      }
      store.headerMedia = { file, dataUrl };
    },
    clearMediaFile() {
      store.headerMedia = null;
    },
  }));

  const MediaHeaderIcon = useMemo(() => {
    return MEDIA_HEADER_ICONS[store.headerMediaType!];
  }, [store.headerMediaType]);

  return (
    <BaseModal
      backdropClassName={styles.backdrop}
      testId="chat_opt_in_modal"
      closeModal={closeModal}
      header={
        <ModalHeader closeModal={closeModal}>
          <div className={styles.headerTitle}>
            {!disableOptIn
              ? t("whatsapp_business_api_agent_page.opt_in.modal.header")
              : t("whatsapp_business_api_agent_page.opt_in.modal.send_template_header")}
          </div>
        </ModalHeader>
      }
      closeOnEscape={false}
      closeOnClickOutside={false}
      className={styles.modal}
      bodyClassName={styles.body}
      footer={
        <ModalButtons
          testId="modal-buttons-submit-button"
          onCancel={() => closeModal()}
          onSubmit={() => closeModal(store.result)}
          isSubmitDisabled={!store.isValid}
        />
      }
    >
      <div className={styles.main}>
        {(numberOfSelectedChats || 0) > 1 && (
          <div>
            {t("whatsapp_business_api_agent_page.selected_chats")} - {numberOfSelectedChats}
          </div>
        )}
        {!disableOptIn && (
          <>
            <div className={styles.message}>{t("whatsapp_business_api_agent_page.opt_in.modal.message1")}</div>
            <div className={styles.secondMessage}>{t("whatsapp_business_api_agent_page.opt_in.modal.message2")}</div>
          </>
        )}
        {Boolean(templates.length) && (
          <>
            <div className={styles.secondMessage}>{t("whatsapp_business_api_agent_page.opt_in.modal.template_name")}</div>
            <Select paddingInline="none" value={store.selectedTemplate} options={store.templates} onChange={store.selectTemplate} />
          </>
        )}
        {store.langs.length > 1 && (
          <>
            <div className={styles.secondMessage}>{t("whatsapp_business_api_agent_page.opt_in.modal.template_lang")}</div>
            <Select paddingInline="none" value={store.selectedLang} options={store.langs} onChange={store.selectLang} />
          </>
        )}

        {store.headerMediaType && (
          <div className={styles.mediaHeaderIndicator}>
            {MediaHeaderIcon && <MediaHeaderIcon size={32} />}
            {t(`whatsapp_business_api_agent_page.opt_in.modal.${store.headerMediaType.toLowerCase()}_header`)}
          </div>
        )}

        <div className={styles.previewMessage}>
          {store.previewTexts.map((text, i) => (
            <div key={i}>{text}</div>
          ))}
          {store.previewButtons.length > 0 && (
            <div className={styles.previewButtons}>
              {store.previewButtons.map((btn, i) => (
                <div key={i} className={styles.previewButton}>{btn.text}</div>
              ))}
            </div>
          )}
        </div>

        {store.headerMediaType && (
          <div className={styles.uploadSection}>
            <div className={styles.uploadLabel}>
              {t(`whatsapp_business_api_agent_page.opt_in.modal.upload_${store.headerMediaType.toLowerCase()}`)}
            </div>
            {store.headerMedia ? (
              <div className={styles.filePreview}>
                <div className={styles.fileInfo}>
                  <div className={styles.fileName}>{store.headerMedia.file.name}</div>
                  <div className={styles.fileSize}>{calFileSizeV2(store.headerMedia.file)}</div>
                </div>
                <div className={styles.removeFileBtn} onClick={() => runInAction(() => store.clearMediaFile())}>
                  <LuX />
                </div>
              </div>
            ) : (
              <FileDropzone
                mode="dataUrl"
                accept={getMediaAccept(store.headerMediaType)}
                onFile={(dataUrl, file) => runInAction(() => store.onMediaFileSelect(dataUrl, file))}
                className={styles.dropzone}
              >
                <div>{t("whatsapp_business_api_agent_page.opt_in.modal.drop_or_click")}</div>
              </FileDropzone>
            )}
          </div>
        )}

        {store.allParams.map((param) => {
          const error = store.touched.has(param.globalIndex) ? store.getParamError(param.value) : undefined;
          return (
            <div key={param.globalIndex} className={styles.paramField}>
              <div className={styles.paramLabel}>{`{{${param.globalIndex}}}:`}</div>
              <TextInput
                paddingInline="none"
                value={param.value}
                onChange={(e) => runInAction(() => (param.component.values[param.paramIndex + 1] = e.currentTarget.value))}
                onBlur={() => runInAction(() => store.setTouched(param.globalIndex))}
                placeholder={param.example || t("whatsapp_business_api_agent_page.opt_in.modal.enter_param_value", { index: param.globalIndex })}
                maxLength={WHATSAPP_PARAM_MAX_LENGTH}
                className={error ? styles.inputError : undefined}
              />
              <div className={styles.paramMeta}>
                {error && <span className={styles.errorText}>{error}</span>}
                <span className={styles.charCount}>
                  {param.value.length}/{WHATSAPP_PARAM_MAX_LENGTH}
                </span>
              </div>
            </div>
          );
        })}
        <ConversationAssignmentCheckboxes
          assignConversationToMe={store.assignConversationToMe}
          setConversationToOpen={store.setConversationToOpen}
          onAssignConversationToMeChange={() => runInAction(() => (store.assignConversationToMe = !store.assignConversationToMe))}
          onSetConversationToOpenChange={() => runInAction(() => (store.setConversationToOpen = !store.setConversationToOpen))}
        />
      </div>
    </BaseModal>
  );
});
